import { useState, useEffect, useRef } from 'react';

interface CartaCardProps {
  autor: string;
  cidade: string;
  data: string;
  trecho: string;
  likes: number;
  tags: string[];
}

export default function CartaCard({ autor, cidade, data, trecho, likes, tags }: CartaCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(likes);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const handleLike = () => {
    if (isLiked) {
      setLikesCount(likesCount - 1);
    } else {
      setLikesCount(likesCount + 1);
    }
    setIsLiked(!isLiked);
  };

  return (
    <div
      ref={cardRef}
      className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-amber-500/40 hover:shadow-xl hover:shadow-amber-500/10 transition-all duration-700 group ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-lg">
            {autor.charAt(0)}
          </div>
          <div>
            <h3 className="text-white font-semibold">{autor}</h3>
            <p className="text-gray-400 text-sm">{cidade}</p>
          </div>
        </div>
        <span className="text-gray-500 text-xs">{data}</span>
      </div>

      {/* Trecho da Carta */}
      <div className="bg-black/30 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4">
        <p className="text-gray-300 italic leading-relaxed text-sm line-clamp-4">
          "{trecho}"
        </p>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs rounded-full"
          >
            #{tag}
          </span>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <button
          onClick={handleLike}
          className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors group/like"
        >
          <i className={`${isLiked ? 'ri-heart-fill text-amber-400' : 'ri-heart-line'} text-xl group-hover/like:scale-110 transition-transform`}></i>
          <span className="text-sm font-medium">{likesCount}</span>
        </button>

        <button className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors">
          <i className="ri-share-line text-lg"></i>
          <span className="text-sm">Compartilhar</span>
        </button>
      </div>
    </div>
  );
}
